/**
 * Browser-side global image URL rewriter
 * Intercepts and rewrites Sanity CDN URLs in the rendered DOM after page load
 * This catches images generated by GatsbyImage and any other image elements
 */

const SANITY_CDN_URL = 'https://cdn.sanity.io'
const MEDIA_BASE_URL = 'https://media.biomarkt.de'

/**
 * Rewrites a single URL
 */
const rewriteUrl = url => {
  if (!url || !url.includes(SANITY_CDN_URL)) return url
  return url.replace(SANITY_CDN_URL, MEDIA_BASE_URL)
}

/**
 * Public helper to rewrite a single Sanity CDN URL to the media proxy.
 * Use this to normalize URLs at render time (e.g. Gatsby GraphQL data on
 * initial load) so they match the already-rewritten Elasticsearch results.
 */
export const rewriteSanityCdnUrl = rewriteUrl

/**
 * Rewrites srcset attribute (comma-separated URLs)
 */
const rewriteSrcSet = srcset => {
  if (!srcset) return srcset
  return srcset
    .split(/\s*,\s*/)
    .map(urlAndSize => {
      const parts = urlAndSize.trim().split(/\s+/)
      const url = parts[0]
      const size = parts.slice(1).join(' ')
      const rewrittenUrl = rewriteUrl(url)
      return size ? `${rewrittenUrl} ${size}` : rewrittenUrl
    })
    .join(', ')
}

/**
 * Rewrites CSS text content to replace Sanity CDN URLs
 * Handles background-image properties in CSS rules
 */
const rewriteCssText = cssText => {
  if (!cssText || !cssText.includes(SANITY_CDN_URL)) return cssText
  // Replace background-image: url('https://cdn.sanity.io...') patterns
  return cssText.replace(
    /background-image:\s*url\((['"]?)https:\/\/cdn\.sanity\.io/g,
    `background-image: url($1${MEDIA_BASE_URL}`
  )
}

/**
 * Initialize global image URL rewriter
 * Runs on page load and MutationObserver for dynamically added images
 */
export const initGlobalImageRewriter = () => {
  // Process all existing images and styles
  const processImages = () => {
    // Handle img elements
    document.querySelectorAll('img[src*="cdn.sanity.io"]').forEach(img => {
      if (img.src && img.src.includes(SANITY_CDN_URL)) {
        img.src = rewriteUrl(img.src)
      }
      if (img.srcset && img.srcset.includes(SANITY_CDN_URL)) {
        img.srcset = rewriteSrcSet(img.srcset)
      }
    })

    // Handle picture source elements
    document
      .querySelectorAll('source[srcset*="cdn.sanity.io"]')
      .forEach(source => {
        if (source.srcset && source.srcset.includes(SANITY_CDN_URL)) {
          source.srcset = rewriteSrcSet(source.srcset)
        }
      })

    // Handle picture source elements with src
    document
      .querySelectorAll('source[src*="cdn.sanity.io"]')
      .forEach(source => {
        if (source.src && source.src.includes(SANITY_CDN_URL)) {
          source.src = rewriteUrl(source.src)
        }
      })

    // Handle background images in style attributes
    document.querySelectorAll('[style*="background-image"]').forEach(el => {
      const bgImage = el.style.backgroundImage
      if (bgImage && bgImage.includes(SANITY_CDN_URL)) {
        el.style.backgroundImage = bgImage.replace(
          /url\((['"]?)https:\/\/cdn\.sanity\.io/g,
          `url($1${MEDIA_BASE_URL}`
        )
      }
    })

    // Handle background images in <style> tags (for gatsby-background-image)
    document.querySelectorAll('style').forEach(styleTag => {
      if (
        styleTag.textContent &&
        styleTag.textContent.includes(SANITY_CDN_URL)
      ) {
        styleTag.textContent = rewriteCssText(styleTag.textContent)
      }
    })
  }

  // Process images on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processImages)
  } else {
    processImages()
  }

  // Watch for dynamically added images and styles.
  // Coalesce bursts of mutations (frequent on a Gatsby SPA) into a single
  // scan per frame instead of running six full-document sweeps per batch.
  let scanScheduled = false
  const scheduleProcessImages = () => {
    if (scanScheduled) return
    scanScheduled = true
    requestAnimationFrame(() => {
      scanScheduled = false
      processImages()
    })
  }

  const observer = new MutationObserver(scheduleProcessImages)

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'style']
  })
}
